import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { BASE_API_URL } from "store/Me";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { Badge } from "components/ui/badge";
import {
  statusBadgeOutlineClass,
  type StatusColor,
} from "shared/utils/statusChipColor";

const SITE_KEY_REQUESTS_QUERY_KEY = "site-key-requests";
const DEFAULT_PAGE_SIZE = 50;

type KeyRequestStatus =
  | "Initial"
  | "Approved"
  | "Denied"
  | "KeyIssueRequested"
  | "KeyIssueSucceeded"
  | "KeyIssueFailed";

interface KeyRequestItem {
  id?: string;
  status?: KeyRequestStatus;
  error?: string;
  credentialType?: string;
  credentialId?: string;
  createdTimestamp?: string;
  updatedTimestamp?: string;
  guestId?: string;
  guestName?: string;
  details?: string;
  note?: string;
}

const STATUS_COLOR: Record<
  string,
  StatusColor
> = {
  KeyIssueSucceeded: "success",
  Denied: "error",
  KeyIssueFailed: "error",
  KeyIssueRequested: "warning",
  Approved: "info",
  Initial: "default",
};

const CREDENTIAL_LABEL: Record<string, string> = {
  remoteOpen: "Remote Open",
  mobileKey: "Mobile Key",
  getPin: "Get PIN",
  physicalKey: "Physical Key",
};

async function getSiteKeyRequests(
  siteId: string,
  page: number,
  pageSize: number,
): Promise<KeyRequestItem[]> {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("pageSize", pageSize.toString());
  const res = await axios.get<KeyRequestItem[]>(
    `${BASE_API_URL}/api/Sites/${siteId}/keyRequests?${params.toString()}`,
  );
  return res.data ?? [];
}

function useGetSiteKeyRequests(
  siteId: string,
  page: number,
  pageSize: number,
) {
  return useQuery({
    queryKey: [SITE_KEY_REQUESTS_QUERY_KEY, siteId, page, pageSize],
    queryFn: () => getSiteKeyRequests(siteId, page, pageSize),
  });
}

export default function SiteKeyRequests({ siteId }: { siteId: string }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading } = useGetSiteKeyRequests(siteId, page, pageSize);
  const requests = data ?? [];

  if (isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500 shadow-sm">
        Loading key requests...
      </div>
    );
  }

  return (
    <AdminTable
      aria-label="Site key requests"
      footer={
        <AdminTablePagination
          currentPage={page}
          pageSize={pageSize}
          visibleRowCount={requests.length}
          isLoading={isLoading}
          onNext={() => setPage((currentPage) => currentPage + 1)}
          onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          disablePrevious={page === 0}
          disableNext={requests.length < pageSize}
        />
      }
    >
        <thead>
          <tr>
            <th scope="col">Updated</th>
            <th scope="col">Guest</th>
            <th scope="col">Status</th>
            <th scope="col">Type</th>
            <th scope="col">Credential ID</th>
            <th scope="col">Error</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req) => {
            const guestName = req.guestName?.trim() || null;
            return (
              <tr key={req.id}>
                <td>
                  <div style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                    {req.updatedTimestamp
                      ? new Date(req.updatedTimestamp).toLocaleString()
                      : "—"}
                  </div>
                  {req.id && (
                    <div style={{ fontSize: 11, color: "gray" }}>{req.id}</div>
                  )}
                </td>
                <td>
                  {guestName && <div>{guestName}</div>}
                  {req.guestId && (
                    <div style={{ fontSize: 11, color: "gray" }}>
                      {req.guestId}
                    </div>
                  )}
                  {!guestName && !req.guestId && "—"}
                </td>
                <td>
                  {req.status && (
                    <Badge
                      className={statusBadgeOutlineClass(
                        STATUS_COLOR[req.status] ?? "default",
                      )}
                      variant="outline"
                    >
                      {req.status}
                    </Badge>
                  )}
                </td>
                <td>
                  {req.credentialType
                    ? (CREDENTIAL_LABEL[req.credentialType] ?? req.credentialType)
                    : "—"}
                </td>
                <td>
                  {req.credentialId ? (
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        maxWidth: 200,
                        overflow: "hidden",
                        fontSize: 12,
                        fontFamily: "monospace",
                      }}
                      title={req.credentialId}
                    >
                      {req.credentialId}
                    </div>
                  ) : "—"}
                </td>
                <td>
                  {req.error ? (
                    <span style={{ color: "red", fontWeight: 500, fontSize: 12 }}>
                      {req.error}
                    </span>
                  ) : "—"}
                </td>
              </tr>
            );
          })}
          {requests.length === 0 && (
            <tr>
              <td colSpan={6} className="py-16 text-center text-sm text-slate-500">
                No key requests found for this site.
              </td>
            </tr>
          )}
        </tbody>
    </AdminTable>
  );
}
